import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { useAuth } from "../../../context/AuthContext";
import { useAguardandoEntregaList } from "../../../hooks/useAguardandoEntrega";
import { useRankingEvents } from "../../../hooks/useRankingEvents";
import { RANKING_SSE_QUERY_KEYS } from "../../../constants/rankingEventQueryKeys";
import type { AguardandoEntregaItem } from "../../../services/aguardandoEntregaService";

interface FilterState {
  displayId: string;
  orderId: string;
}

interface AguardandoEntregaContextType {
  filters: FilterState;
  filterErrors: Record<string, string>;
  isFilterModalOpen: boolean;
  setIsFilterModalOpen: (open: boolean) => void;

  items: AguardandoEntregaItem[];
  total: number;
  isInitialLoading: boolean;
  isRefetching: boolean;

  handleFieldChange: (name: string, value: string) => void;
  handleSearch: () => boolean;
  handleClear: () => void;
  clearFilterErrors: () => void;
}

const AguardandoEntregaContext = createContext<
  AguardandoEntregaContextType | undefined
>(undefined);

export const AguardandoEntregaProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<FilterState>({
    displayId: "",
    orderId: "",
  });
  const [searchFilters, setSearchFilters] = useState<FilterState>({
    displayId: "",
    orderId: "",
  });
  const [filterErrors, setFilterErrors] = useState<Record<string, string>>({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [liveUpdatesEnabled, setLiveUpdatesEnabled] = useState(false);
  const [entregaSseAvailable, setEntregaSseAvailable] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setLiveUpdatesEnabled(true), 1500);
    return () => window.clearTimeout(timer);
  }, []);

  useRankingEvents("entrega", user?.lojaId ?? null, {
    queryKeys: RANKING_SSE_QUERY_KEYS.aguardandoEntregaPage,
    enabled: liveUpdatesEnabled,
    onAvailabilityChange: setEntregaSseAvailable,
  });

  const displayIdParam = searchFilters.displayId?.trim() || undefined;
  const orderIdParam = searchFilters.orderId?.trim() || undefined;

  const { data, isPending, isFetching } = useAguardandoEntregaList(
    user?.lojaId,
    displayIdParam,
    orderIdParam,
    {
      refetchInterval: entregaSseAvailable ? false : 30_000,
      refetchOnWindowFocus: false,
    }
  );

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const isInitialLoading = isPending;
  const isRefetching = isFetching && !isPending;

  const handleFieldChange = useCallback((name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setFilterErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleSearch = useCallback((): boolean => {
    setFilterErrors({});
    setSearchFilters(filters);
    return true;
  }, [filters]);

  const clearFilterErrors = useCallback(() => setFilterErrors({}), []);

  const handleClear = useCallback(() => {
    setFilters({ displayId: "", orderId: "" });
    setSearchFilters({ displayId: "", orderId: "" });
  }, []);

  return (
    <AguardandoEntregaContext.Provider
      value={{
        filters,
        filterErrors,
        isFilterModalOpen,
        setIsFilterModalOpen,
        items,
        total,
        isInitialLoading,
        isRefetching,
        handleFieldChange,
        handleSearch,
        handleClear,
        clearFilterErrors,
      }}
    >
      {children}
    </AguardandoEntregaContext.Provider>
  );
};

export const useAguardandoEntregaPage = () => {
  const ctx = useContext(AguardandoEntregaContext);
  if (!ctx) {
    throw new Error(
      "useAguardandoEntregaPage must be used within AguardandoEntregaProvider"
    );
  }
  return ctx;
};
